import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DTypeForm, DTypeFormSelectField } from '@grupo-constant/next-dtype/form';
import { DType, SelectOption, SelectOptionGroup } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { cloneDeep } from 'lodash-es';
import { DialogsService } from '../dialogs.service';
import { TaskTypeFormDialogConfig } from './task-type-form-dialog.types';
import { MasterDataService } from '../../core/master-data/master-data.service';
import { firstValueFrom } from 'rxjs';
import { NextTaskType, NextTaskTypeForm } from '../../core/task-type/task-type.types';
import { DTypeService } from '../../core/dtype/dtype.service';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';
import { FuseConfirmationDialogComponent } from '../../../@fuse/services/confirmation/dialog/dialog.component';

@Component({
    selector: 'app-task-type-form-dialog',
    templateUrl: './task-type-form-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            select-group-field-component {
                disabled: true;
                font: bold;
            }
        `
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TaskTypeFormDialogComponent implements OnInit {

    dTypeForm: DTypeForm;
    item: NextTaskTypeForm;
    options: string[] = [];

    workdayOptions: SelectOption[] = [];

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            taskType: NextTaskType;
            item: NextTaskTypeForm;
            dType: DType;
            config: TaskTypeFormDialogConfig;
            update: boolean;
            formsUris: string[];
        },
        private dialogsService: DialogsService,
        public dialogRef: MatDialogRef<TaskTypeFormDialogComponent>,
        private dTypeService: DTypeService,
        private masterDataService: MasterDataService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService
    ) { }

    async ngOnInit() {
        this.item = cloneDeep(this.data.item ?? {});
        this.dTypeForm = this.dTypeService.getDtypeFormFromDtype(
            this.data.dType,
            this.item,
            {
                readonly: false,
                isUpdate: this.data.update
            }
        );
        for (const group of (this.dTypeForm.fields.formUri as DTypeFormSelectField).groups ?? []) {
            for (const option of group.option) {
                if (this.data.item?.formUri !== option.code && this.data.formsUris.some(u => u === option.code)) {
                    option.disabled = true;
                }
            }
        }
        if (this.data.taskType.workDayTypeUri) {
            this.workdayOptions = await firstValueFrom(this.masterDataService.retrieveMasterDataAsSelectOptions(this.data.taskType.workDayTypeUri)) ?? [];
        }
    }

    addNameToGroup(group: SelectOptionGroup) {
        if (group.option[0].code.includes('Service=')) {
            return this.transloco.translate('service') + ': ' + group.value;
        } else if (group.option[0].code.includes('Project=')) {
            return this.transloco.translate('project') + ': ' + group.value;
        } else if (group.option[0].code.includes('Customer=')) {
            return this.transloco.translate('customer') + ': ' + group.value;
        } else if (group.option[0].code.includes('Brands=')) {
            return this.transloco.translate('brand') + ': ' + group.value;
        } else {
            return 'OU: ' + group.value;
        }
    }

    save() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('saveDataItemConfirm'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                const form = this.dTypeForm.control;
                form.markAllAsTouched();
                if (form.valid) {
                    this.data.formsUris.push(this.dTypeForm.control.value.formUri);
                    this.closeDialog({ ...form.getRawValue(), visibleWorkDayTypeId: this.item.visibleWorkDayTypeId });
                } else {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('errorsInForm')
                    });
                }
            }
        });
    }

    closeDialog(result?: any): void {
        this.dialogRef.close(result);
    }

    clear() {
        this.dTypeForm.control.reset(this.data.item ?? {});
    }

    private openConfirmDialog(message: string): MatDialogRef<FuseConfirmationDialogComponent, any> {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }

}
